/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { HTMLAttributeAnchorTarget } from 'react';
import React from 'react';
import styles from './index.module.less';
import * as sitePath from '@/utils/function/sitePath';
import { companyLinks, FooterContext, productCategoryLinks, supportLinks } from '../../common';

import type { IProductRecommendItem } from '@/interface';
import { getFooterRecommendProducts } from '@/api/client/product';
import CompFooterLinkItem from './FooterLinkItem';

export interface IFooterLinkItem {
    key: string;
    link: string;
    strong?: boolean;
    target?: HTMLAttributeAnchorTarget;
    rel?: string;
    title?: string;
}

const CompFooterLinkGroups: React.FC = () => {
    const { getTextByKey, lang, country, isDistributor } = React.useContext(FooterContext);

    const [recommendedProducts, setRecommendedProducts] = React.useState<IProductRecommendItem[]>([]);

    const [expandKey, setExpandKey] = React.useState<string>();
    const fetchRecommendedProducts = React.useCallback(async () => {
        try {
            const res = await getFooterRecommendProducts(lang);
            setRecommendedProducts(res.data);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }, [lang]);

    const footerLinkGroups = React.useMemo(() => {
        return [
            {
                key: 'product-links',
                links: [
                    ...recommendedProducts.map((item) => {
                        return {
                            key: item.id,
                            link: item.href,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            strong: false,
                            title: item.title,
                        };
                    }),
                    ...productCategoryLinks,
                ] as IFooterLinkItem[],
            },
            {
                key: 'support-links',
                links: supportLinks,
            },
            {
                key: 'company-links',
                links: companyLinks,
            },
            {
                key: 'programs-links',
                links: [
                    {
                        key: 'affiliate-program',
                        link: sitePath.getOfficialSitePath('/affiliate'),
                    },
                    {
                        key: 'partner-program',
                        link: sitePath.getOfficialSitePath('/distributor-reseller'),
                    },
                    ...(isDistributor
                        ? []
                        : [
                            {
                                key: 'shop-for-business',
                                link: sitePath.getOfficialSitePath('/for-business'),
                            },
                        ]),
                    {
                        key: 'referral-program',
                        link: sitePath.getOfficialSitePath('/referral'),
                    },
                    {
                        key: 'reolink-trial',
                        link: sitePath.getOfficialSitePath('/reolink-trial'),
                    },
                    {
                        key: 'reolink-in-action',
                        link: sitePath.getOfficialSitePath('/reolink-in-action'),
                    },
                    ...(country === 'US' || country === 'CA'
                        ? [
                            {
                                key: 'student-discount',
                                link: 'https://services.sheerid.com/verify/64e96f5ffdb00056dcfa9c7d/?layout=landing',
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            },
                            {
                                key: 'teacher-discount',
                                link: 'https://services.sheerid.com/verify/64db29933bc3805b57c9ff64/?layout=landing',
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            },
                        ]
                        : []),
                    ...(country === 'US'
                        ? [
                            {
                                key: 'military-discounts',
                                link: sitePath.getOfficialSitePath('/lp/military-discounts/'),
                            },
                        ]
                        : []),
                ],
            },
        ];
    }, [country, recommendedProducts]);

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchRecommendedProducts();
    }, [fetchRecommendedProducts]);

    return (
        <ul className={ styles['footer-link-groups'] }>
            {footerLinkGroups.map((linkGroup) => {
                return (
                    <CompFooterLinkItem
                        key={ linkGroup.key }
                        items={ linkGroup.links }
                        title={ getTextByKey(linkGroup.key) }
                        isExpend={ expandKey === linkGroup.key }
                        onExpendChange={ (expand) => {
                            setExpandKey(expand ? linkGroup.key : undefined);
                        } }
                    />
                );
            })}
        </ul>
    );
};

export default CompFooterLinkGroups;
